import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Which messages in a ticket's conversation are open. On first load only the
 * newest is; the rest show as one-line previews. A message that arrives while
 * the page is open (your own reply, or someone else's over the socket) opens
 * too, since it's the one you want to read. Nothing is remembered across
 * visits.
 *
 * `ids` must be in display order, newest first. `resetKey` is the ticket id:
 * moving to another ticket starts over.
 */
export function useMessageCollapse(ids: string[], resetKey: string | undefined) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const seen = useRef<Set<string> | null>(null);
  const idsKey = ids.join(',');

  useEffect(() => {
    seen.current = null;
    setExpanded(new Set());
  }, [resetKey]);

  useEffect(() => {
    if (ids.length === 0) return;
    if (seen.current === null) {
      // First batch for this ticket: open the newest message only.
      seen.current = new Set(ids);
      setExpanded(new Set([ids[0]]));
      return;
    }
    const fresh = ids.filter(id => !seen.current!.has(id));
    if (fresh.length === 0) return;
    for (const id of fresh) seen.current.add(id);
    setExpanded(prev => new Set([...prev, ...fresh]));
    // idsKey stands in for ids — a new array each render, same contents.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey, resetKey]);

  const toggle = useCallback((id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => setExpanded(new Set(ids)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idsKey]);
  const collapseAll = useCallback(() => setExpanded(new Set()), []);

  return {
    isExpanded: (id: string) => expanded.has(id),
    toggle,
    expandAll,
    collapseAll,
  };
}
